import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Checkbox, Drawer, Flex, Form, Input, InputNumber, Modal, Popconfirm, Radio, Select, Space, Switch, Table, Tag, Tooltip, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { CLIENT_PAGING } from "@/components/list/useListPage";
import { SkuSelect } from "../catalog/mapping";
import { PlatformLabel } from "../channels/PlatformBadge";
import { ShipBy } from "../orders/ShipBy";
import { COURIER_KINDS, type CourierKind } from "../orders/CourierKind";
import { SkuTags } from "./Counts";
import { MethodTag, NoWms, useWmsWarehouse, type Pool } from "./common";
import { usePoll } from "@/components/tabs/WorkTabs";

type Method = Pool["method"];
type Rules = Schemas["PoolRules"];
type Shape = NonNullable<Rules["shapes"]>[number];
type Platform = Schemas["ChannelPlatformName"];
const methods: Method[] = ["auto", "single", "batch", "cluster"];
const shapes: Shape[] = ["single_pcs", "single_sku", "multi_sku"];
const platforms: Platform[] = ["shopee", "tiktok", "tokopedia", "lazada", "blibli"];
type Schedule = Schemas["WaveSchedule"];
type ReleaseMode = NonNullable<Schedule["mode"]>;
// Release times every half hour.
const CLOCK = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`).map((v) => ({ value: v, label: v }));

/**
 * Gudang > Pool order (Plan §6.9): open orders split by rules (courier, SLA,
 * brand, SKU, store, shape), each pool released as waves of single-pcs,
 * batch or cluster picking.
 */
export function Pools() {
  const { t, formatNumber } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const { current, loading } = useWmsWarehouse();
  const [editing, setEditing] = useState<Pool | "new" | null>(null);
  const [viewing, setViewing] = useState<Pool | null>(null);
  const [releasing, setReleasing] = useState<Pool | null>(null);
  const manage = can("warehouse.manage");

  const list = useQuery({
    queryKey: ["pools", current?.id],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/pools", { params: { path: { warehouseID: current!.id } } })),
    enabled: !!current,
    refetchInterval: usePoll(30_000),
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/pools/{poolID}", { params: { path: { poolID: id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["pools"] }),
    onError: (err) => void message.error(errorText(err)),
  });

  if (!loading && !current) return <NoWms />;
  const nameOf = (p: Pool) => p.name || t("wms.pool.other");
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("wms.pool.title")}
        </Typography.Title>
        {manage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing("new")}>
            {t("wms.pool.add")}
          </Button>
        )}
      </Flex>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
      </Flex>
      {(list.data?.blocked ?? 0) > 0 && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("wms.pool.blocked", { n: list.data!.blocked })} />}
      <Table<Pool>
        rowKey={(p) => p.id ?? "other"}
        loading={list.isPending}
        dataSource={list.data?.items ?? []}
        pagination={CLIENT_PAGING}
        scroll={{ x: 1000 }}
        columns={[
          {
            title: t("wms.pool.name"),
            render: (_, p) => (
              <Space orientation="vertical" size={2}>
                <Space size={6}>
                  <Typography.Text strong>{nameOf(p)}</Typography.Text>
                  {p.id && !p.active && <Tag>{t("wms.pool.inactive")}</Tag>}
                </Space>
                <RulesSummary pool={p} />
                <AutoRelease pool={p} />
              </Space>
            ),
          },
          { title: t("wms.pool.method"), dataIndex: "method", width: 110, render: (m: Method) => <MethodTag method={m} /> },
          {
            title: t("wms.pool.orders"),
            width: 120,
            align: "right",
            render: (_, p) => (
              <Space orientation="vertical" size={0} style={{ alignItems: "flex-end" }}>
                <Typography.Text strong className="z-num">{formatNumber(p.stats.orders)}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t("wms.pool.units", { n: formatNumber(p.stats.units) })}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: t("wms.pool.split"),
            width: 230,
            render: (_, p) => (
              <Space size={4} wrap>
                {p.stats.single > 0 && <Tag color="green">{t("wms.method.single")} {p.stats.single}</Tag>}
                {p.stats.batch > 0 && <Tag color="geekblue">{t("wms.method.batch")} {p.stats.batch}</Tag>}
                {p.stats.cluster > 0 && <Tag color="purple">{t("wms.method.cluster")} {p.stats.cluster}</Tag>}
              </Space>
            ),
          },
          {
            title: t("orders.shipBy"),
            width: 170,
            render: (_, p) => (
              <Space size={4} wrap>
                {p.stats.overdue > 0 && <Tag color="error">{t("wms.pool.overdue", { n: p.stats.overdue })}</Tag>}
                {p.stats.dueSoon > 0 && <Tag color="warning">{t("wms.pool.dueSoon", { n: p.stats.dueSoon })}</Tag>}
                {!p.stats.overdue && !p.stats.dueSoon && <ShipBy at={p.stats.earliestShipBy} />}
              </Space>
            ),
          },
          {
            width: 250,
            render: (_, p) => (
              <Space>
                <Button size="small" disabled={p.stats.orders === 0} onClick={() => setViewing(p)}>
                  {t("wms.pool.viewOrders")}
                </Button>
                {manage && (
                  <Button size="small" type="primary" disabled={p.stats.orders === 0} onClick={() => setReleasing(p)}>
                    {t("wms.wave.release")}
                  </Button>
                )}
                {manage && p.id && (
                  <>
                    <Tooltip title={t("common.edit")}>
                      <Button size="small" type="text" icon={<EditOutlined />} aria-label={t("common.edit")} onClick={() => setEditing(p)} />
                    </Tooltip>
                    <Popconfirm title={t("wms.pool.deleteConfirm", { name: p.name })} okText={t("common.delete")} cancelText={t("common.cancel")} onConfirm={() => remove.mutate(p.id!)}>
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label={t("common.delete")} />
                    </Popconfirm>
                  </>
                )}
              </Space>
            ),
          },
        ]}
      />
      <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
        {t("wms.pool.help")}
      </Typography.Paragraph>
      {current && (
        <>
          <PoolModal
            warehouseId={current.id}
            pool={editing}
            carriers={list.data?.carriers ?? []}
            brands={list.data?.brands ?? []}
            zones={list.data?.zones ?? []}
            onClose={() => setEditing(null)}
          />
          <PoolOrders warehouseId={current.id} pool={viewing} onClose={() => setViewing(null)} />
          <ReleaseModal warehouseId={current.id} pool={releasing} onClose={() => setReleasing(null)} />
        </>
      )}
    </>
  );
}

/** When the pool releases itself next, and how the last run went. */
function AutoRelease({ pool }: { pool: Pool }) {
  const { t, tDynamic, formatDateTime } = useI18n();
  const s = pool.schedule;
  if (!pool.id || !s || s.mode === "off") return null;
  const r = pool.lastReleaseResult;
  const [head, ref] = r.split(":");
  const last = !r ? "" : head === "released" ? t("wms.pool.auto.lastReleased", { wave: ref ?? "" }) : tDynamic(`wms.pool.auto.last.${head}`, "wms.pool.auto.last.failed");
  return (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {s.mode === "times" ? t("wms.pool.auto.atTimes", { times: (s.times ?? []).join(", ") }) : t("wms.pool.auto.every", { n: s.everyHours ?? 2, from: s.from ?? "", until: s.until ?? "" })}
      {pool.nextReleaseAt && pool.active ? ` · ${t("wms.pool.auto.next", { at: formatDateTime(pool.nextReleaseAt) })}` : ""}
      {last ? ` · ${last}` : ""}
    </Typography.Text>
  );
}

/** The rules of a pool in a line of tags. */
function RulesSummary({ pool }: { pool: Pool }) {
  const { t } = useI18n();
  const r = pool.rules;
  if (!pool.id) return <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t("wms.pool.otherHelp")}</Typography.Text>;
  const tags: string[] = [];
  if (r.carriers?.length) tags.push(r.carriers.join(", "));
  if (r.courierKinds?.length) tags.push(r.courierKinds.map((k) => t(`courier.kind.${k}`)).join(", "));
  if (r.shipWithinHours != null) tags.push(r.shipWithinHours === 0 ? t("wms.pool.rule.late") : t("wms.pool.rule.within", { n: r.shipWithinHours }));
  if (r.platforms?.length) tags.push(r.platforms.map((p) => t(`platform.${p}`)).join(", "));
  if (r.connectionIds?.length) tags.push(t("wms.pool.rule.stores", { n: r.connectionIds.length }));
  if (r.brands?.length) tags.push(r.brands.join(", "));
  if (r.skuIds?.length) tags.push(t(r.skuMatch === "only" ? "wms.pool.rule.skusOnly" : "wms.pool.rule.skus", { n: r.skuIds.length }));
  if (r.shapes?.length) tags.push(r.shapes.map((s) => t(`wms.pool.shape.${s}`)).join(", "));
  if (r.maxUnits) tags.push(t("wms.pool.rule.maxUnits", { n: r.maxUnits }));
  if (r.singleZone) tags.push(r.zones?.length ? t("wms.pool.rule.singleZoneOf", { zones: r.zones.join(", ") }) : t("wms.pool.rule.singleZone"));
  else if (r.zones?.length) tags.push(t("wms.pool.rule.zones", { zones: r.zones.join(", ") }));
  if (tags.length === 0) return <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t("wms.pool.rule.none")}</Typography.Text>;
  return (
    <Space size={4} wrap>
      {tags.map((x) => (
        <Tag key={x} style={{ marginInlineEnd: 0 }}>
          {x}
        </Tag>
      ))}
    </Space>
  );
}

type PoolValues = {
  name: string;
  priority: number;
  active: boolean;
  method: Method;
  cartSlots: number;
  maxOrders: number;
  batchMin: number;
  carriers: string[];
  courierKinds: CourierKind[];
  platforms: Platform[];
  connectionIds: string[];
  brands: string[];
  skuMatch: "any" | "only";
  shapes: Shape[];
  shipWithinHours: number | null;
  maxUnits: number | null;
  zones: string[];
  singleZone: boolean;
  autoMode: ReleaseMode;
  autoTimes: string[];
  autoEvery: number;
  autoFrom: string;
  autoUntil: string;
  autoMin: number;
};

function PoolModal({ warehouseId, pool, carriers, brands, zones, onClose }: {
  warehouseId: string;
  pool: Pool | "new" | null;
  carriers: string[];
  brands: string[];
  zones: string[];
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<PoolValues>();
  const method = Form.useWatch("method", form);
  const autoMode = Form.useWatch("autoMode", form);
  const [skus, setSkus] = useState<string[]>([]);
  const [pick, setPick] = useState<string>();
  const [opened, setOpened] = useState<Pool | "new" | null>(null);
  if (pool !== opened) {
    // Load the form each time the modal opens on a pool.
    setOpened(pool);
    setSkus(pool && pool !== "new" ? (pool.rules.skuIds ?? []) : []);
  }
  const stores = useQuery({
    queryKey: ["channels", "connections"],
    queryFn: () => unwrap(api.GET("/v1/channels/connections")),
    enabled: pool !== null && can("channels.view"),
  });
  const existing = pool && pool !== "new" ? pool : null;
  const initial: PoolValues = existing
    ? {
        name: existing.name,
        priority: existing.priority,
        active: existing.active,
        method: existing.method,
        cartSlots: existing.cartSlots,
        maxOrders: existing.maxOrders,
        batchMin: existing.batchMin,
        carriers: existing.rules.carriers ?? [],
        courierKinds: existing.rules.courierKinds ?? [],
        platforms: existing.rules.platforms ?? [],
        connectionIds: existing.rules.connectionIds ?? [],
        brands: existing.rules.brands ?? [],
        skuMatch: existing.rules.skuMatch ?? "any",
        shapes: existing.rules.shapes ?? [],
        shipWithinHours: existing.rules.shipWithinHours ?? null,
        maxUnits: existing.rules.maxUnits ?? null,
        zones: existing.rules.zones ?? [],
        singleZone: existing.rules.singleZone ?? false,
        autoMode: existing.schedule?.mode ?? "off",
        autoTimes: existing.schedule?.times ?? [],
        autoEvery: existing.schedule?.everyHours ?? 2,
        autoFrom: existing.schedule?.from ?? "08:00",
        autoUntil: existing.schedule?.until ?? "20:00",
        autoMin: existing.schedule?.minOrders ?? 1,
      }
    : {
        name: "",
        priority: 10,
        active: true,
        method: "auto",
        cartSlots: 12,
        maxOrders: 200,
        batchMin: 2,
        carriers: [],
        courierKinds: [],
        platforms: [],
        connectionIds: [],
        brands: [],
        skuMatch: "any",
        shapes: [],
        shipWithinHours: null,
        maxUnits: null,
        zones: [],
        singleZone: false,
        autoMode: "off",
        autoTimes: [],
        autoEvery: 2,
        autoFrom: "08:00",
        autoUntil: "20:00",
        autoMin: 1,
      };

  const save = useMutation({
    mutationFn: (v: PoolValues) => {
      const rules: Rules = {
        ...(v.carriers.length ? { carriers: v.carriers } : {}),
        ...(v.courierKinds.length ? { courierKinds: v.courierKinds } : {}),
        ...(v.platforms.length ? { platforms: v.platforms } : {}),
        ...(v.connectionIds.length ? { connectionIds: v.connectionIds } : {}),
        ...(v.brands.length ? { brands: v.brands } : {}),
        ...(skus.length ? { skuIds: skus, skuMatch: v.skuMatch } : {}),
        ...(v.shapes.length ? { shapes: v.shapes } : {}),
        ...(v.shipWithinHours != null ? { shipWithinHours: v.shipWithinHours } : {}),
        ...(v.maxUnits ? { maxUnits: v.maxUnits } : {}),
        ...(v.zones.length ? { zones: v.zones } : {}),
        ...(v.singleZone ? { singleZone: true } : {}),
      };
      const schedule: Schedule = { mode: v.autoMode, times: v.autoTimes, everyHours: v.autoEvery, from: v.autoFrom, until: v.autoUntil, minOrders: v.autoMin };
      const body = { name: v.name.trim(), priority: v.priority, active: v.active, method: v.method, cartSlots: v.cartSlots, maxOrders: v.maxOrders, batchMin: v.batchMin, rules, schedule };
      return existing
        ? unwrap(api.PUT("/v1/pools/{poolID}", { params: { path: { poolID: existing.id! } }, body }))
        : unwrap(api.POST("/v1/warehouses/{warehouseID}/pools", { params: { path: { warehouseID: warehouseId } }, body }));
    },
    onSuccess: () => {
      void message.success(t("wms.pool.saved"));
      void queryClient.invalidateQueries({ queryKey: ["pools"] });
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });

  return (
    <Modal
      open={pool !== null}
      width={760}
      title={existing ? t("wms.pool.editTitle", { name: existing.name }) : t("wms.pool.add")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark={false} initialValues={initial} onFinish={(v) => save.mutate(v)}>
        <Flex gap={12}>
          <Form.Item name="name" label={t("wms.pool.name")} style={{ flex: 1 }} rules={[{ required: true, whitespace: true, message: t("field.required") }]}>
            <Input maxLength={80} autoFocus />
          </Form.Item>
          <Form.Item name="priority" label={t("wms.pool.priority")} tooltip={t("wms.pool.priorityHelp")} style={{ width: 120 }}>
            <InputNumber min={0} max={10_000} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="active" label={t("wms.pool.active")} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Flex>

        <Typography.Title level={4} style={{ fontSize: 15, marginTop: 0 }}>
          {t("wms.pool.rules")}
        </Typography.Title>
        <Typography.Paragraph type="secondary">{t("wms.pool.rulesHelp")}</Typography.Paragraph>
        <Flex gap={12} wrap>
          <Form.Item name="carriers" label={t("orders.carrier")} style={{ flex: "1 1 300px" }}>
            <Select mode="tags" allowClear options={carriers.map((c) => ({ value: c, label: c }))} placeholder={t("wms.pool.any")} />
          </Form.Item>
          <Form.Item name="courierKinds" label={t("courier.kind")} extra={t("wms.pool.courierKindsHelp")} style={{ flex: "1 1 220px" }}>
            <Select mode="multiple" allowClear options={COURIER_KINDS.map((k) => ({ value: k, label: t(`courier.kind.${k}`) }))} placeholder={t("wms.pool.any")} />
          </Form.Item>
          <Form.Item name="shipWithinHours" label={t("wms.pool.shipWithin")} extra={t("wms.pool.shipWithinHelp")} style={{ width: 200 }}>
            <InputNumber min={0} max={720} precision={0} suffix={t("wms.pool.hours")} placeholder={t("wms.pool.any")} style={{ width: "100%" }} />
          </Form.Item>
        </Flex>
        <Flex gap={12} wrap>
          <Form.Item name="platforms" label={t("wms.pool.platforms")} style={{ flex: "1 1 240px" }}>
            <Select mode="multiple" allowClear options={platforms.map((p) => ({ value: p, label: <PlatformLabel platform={p} /> }))} placeholder={t("wms.pool.any")} />
          </Form.Item>
          {can("channels.view") && (
            <Form.Item name="connectionIds" label={t("wms.pool.stores")} style={{ flex: "1 1 240px" }}>
              <Select mode="multiple" allowClear loading={stores.isPending} options={(stores.data?.items ?? []).map((c) => ({ value: c.id, label: c.name }))} placeholder={t("wms.pool.any")} />
            </Form.Item>
          )}
        </Flex>
        <Form.Item name="brands" label={t("catalog.brand")}>
          <Select mode="tags" allowClear options={brands.map((b) => ({ value: b, label: b }))} placeholder={t("wms.pool.any")} />
        </Form.Item>
        <Form.Item label={t("wms.pool.skus")}>
          <Flex gap={8}>
            <div style={{ flex: 1 }}>
              <SkuSelect value={pick} onChange={setPick} />
            </div>
            <Button
              disabled={!pick}
              onClick={() => {
                if (pick && !skus.includes(pick)) setSkus([...skus, pick]);
                setPick(undefined);
              }}
            >
              {t("common.add")}
            </Button>
          </Flex>
          {skus.length > 0 && (
            <>
              <SkuTags ids={skus} onRemove={(id) => setSkus(skus.filter((x) => x !== id))} />
              <Form.Item name="skuMatch" noStyle>
                <Radio.Group
                  style={{ marginTop: 8 }}
                  options={[
                    { value: "any", label: t("wms.pool.skuAny") },
                    { value: "only", label: t("wms.pool.skuOnly") },
                  ]}
                />
              </Form.Item>
            </>
          )}
        </Form.Item>
        <Flex gap={12} wrap>
          <Form.Item name="shapes" label={t("wms.pool.shapes")} style={{ flex: 1 }}>
            <Checkbox.Group options={shapes.map((s) => ({ value: s, label: t(`wms.pool.shape.${s}`) }))} />
          </Form.Item>
          <Form.Item name="maxUnits" label={t("wms.pool.maxUnits")} style={{ width: 160 }}>
            <InputNumber min={1} max={10_000} precision={0} placeholder={t("wms.pool.any")} style={{ width: "100%" }} />
          </Form.Item>
        </Flex>
        {zones.length > 0 && (
          <Flex gap={12} wrap align="start">
            <Form.Item name="zones" label={t("wms.pool.zones")} extra={t("wms.pool.zonesHelp")} style={{ flex: "1 1 240px" }}>
              <Select mode="multiple" allowClear options={zones.map((z) => ({ value: z, label: z }))} placeholder={t("wms.pool.any")} />
            </Form.Item>
            <Form.Item name="singleZone" valuePropName="checked" label=" " extra={t("wms.pool.singleZoneHelp")} style={{ flex: "1 1 240px" }}>
              <Checkbox>{t("wms.pool.singleZone")}</Checkbox>
            </Form.Item>
          </Flex>
        )}

        <Typography.Title level={4} style={{ fontSize: 15 }}>
          {t("wms.pool.waves")}
        </Typography.Title>
        <Form.Item name="method" label={t("wms.pool.method")}>
          <Radio.Group style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {methods.map((m) => (
              <Radio key={m} value={m}>
                <Space orientation="vertical" size={0}>
                  <span>{t(`wms.method.${m}`)}</span>
                  <Typography.Text type="secondary">{t(`wms.method.${m}Help`)}</Typography.Text>
                </Space>
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
        <Flex gap={12} wrap>
          {(method === "auto" || method === "cluster") && (
            <Form.Item name="cartSlots" label={t("wms.pool.cartSlots")} tooltip={t("wms.pool.cartSlotsHelp")} style={{ width: 160 }}>
              <InputNumber min={1} max={100} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          )}
          {(method === "auto" || method === "batch") && (
            <Form.Item name="batchMin" label={t("wms.pool.batchMin")} tooltip={t("wms.pool.batchMinHelp")} style={{ width: 160 }}>
              <InputNumber min={2} max={1000} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          )}
          <Form.Item name="maxOrders" label={t("wms.pool.maxOrders")} style={{ width: 160 }}>
            <InputNumber min={1} max={5000} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </Flex>

        <Typography.Title level={4} style={{ fontSize: 15 }}>
          {t("wms.pool.auto.title")}
        </Typography.Title>
        <Form.Item name="autoMode" extra={t("wms.pool.auto.help")}>
          <Radio.Group
            options={[
              { value: "off", label: t("wms.pool.auto.off") },
              { value: "times", label: t("wms.pool.auto.times") },
              { value: "interval", label: t("wms.pool.auto.interval") },
            ]}
          />
        </Form.Item>
        {autoMode === "times" && (
          <Form.Item name="autoTimes" label={t("wms.pool.auto.timesLabel")} extra={t("wms.pool.auto.timesHelp")} rules={[{ required: true, message: t("field.required") }]}>
            <Select mode="multiple" options={CLOCK} placeholder="09:00, 13:00, 16:00" />
          </Form.Item>
        )}
        {autoMode === "interval" && (
          <Flex gap={12} wrap>
            <Form.Item name="autoEvery" label={t("wms.pool.auto.everyLabel")} style={{ width: 160 }}>
              <InputNumber min={1} max={24} precision={0} suffix={t("wms.pool.auto.hours")} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="autoFrom" label={t("wms.pool.auto.from")} style={{ width: 140 }}>
              <Select options={CLOCK} />
            </Form.Item>
            <Form.Item name="autoUntil" label={t("wms.pool.auto.until")} style={{ width: 140 }}>
              <Select options={CLOCK} />
            </Form.Item>
          </Flex>
        )}
        {autoMode !== "off" && (
          <Form.Item name="autoMin" label={t("wms.pool.auto.minOrders")} tooltip={t("wms.pool.auto.minOrdersHelp")} style={{ width: 200 }}>
            <InputNumber min={1} max={5000} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

function PoolOrders({ warehouseId, pool, onClose }: { warehouseId: string; pool: Pool | null; onClose: () => void }) {
  const { t } = useI18n();
  const orders = useQuery({
    queryKey: ["pools", "orders", warehouseId, pool?.id ?? "other"],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/pools/{poolID}/orders", { params: { path: { warehouseID: warehouseId, poolID: pool!.id ?? "other" } } })),
    enabled: pool !== null,
  });
  return (
    <Drawer open={pool !== null} onClose={onClose} size="large" title={pool ? pool.name || t("wms.pool.other") : ""} destroyOnHidden>
      <Table
        size="small"
        rowKey="id"
        loading={orders.isPending}
        dataSource={orders.data?.items ?? []}
        pagination={false}
        columns={[
          {
            title: t("orders.number"),
            dataIndex: "externalId",
            render: (v: string, o) => (
              <Space orientation="vertical" size={0}>
                <span className="z-num">{v}</span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t(`platform.${o.platform}`)} · {o.carrier || "—"}
                </Typography.Text>
              </Space>
            ),
          },
          { title: t("orders.items"), dataIndex: "items", render: (v: string) => <span className="z-num">{v}</span> },
          { title: t("wms.pool.shapes"), dataIndex: "shape", width: 120, render: (s: Shape) => <Tag>{t(`wms.pool.shape.${s}`)}</Tag> },
          { title: t("orders.shipBy"), dataIndex: "shipBy", width: 150, render: (v: string | null) => <ShipBy at={v} /> },
        ]}
      />
    </Drawer>
  );
}

function ReleaseModal({ warehouseId, pool, onClose }: { warehouseId: string; pool: Pool | null; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form] = Form.useForm<{ method: Method; maxOrders: number }>();
  const release = useMutation({
    mutationFn: (v: { method: Method; maxOrders: number }) =>
      unwrap(api.POST("/v1/waves", { body: { warehouseId, poolId: pool!.id ?? null, method: v.method, maxOrders: v.maxOrders } })),
    onSuccess: (wave) => {
      for (const k of ["pools", "waves", "tasks", "inventory", "orders"]) void queryClient.invalidateQueries({ queryKey: [k] });
      onClose();
      navigate(`/app/warehouse/waves/${wave.id}`, { state: { skipped: wave.skipped ?? [] } });
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open={pool !== null}
      title={pool ? t("wms.wave.releaseTitle", { pool: pool.name || t("wms.pool.other") }) : ""}
      okText={t("wms.wave.release")}
      cancelText={t("common.cancel")}
      confirmLoading={release.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {pool && (
        <Form form={form} layout="vertical" initialValues={{ method: pool.method, maxOrders: Math.min(pool.maxOrders, pool.stats.orders) }} onFinish={(v) => release.mutate(v)}>
          <Typography.Paragraph>{t("wms.wave.releaseBody", { n: pool.stats.orders })}</Typography.Paragraph>
          <Form.Item name="method" label={t("wms.pool.method")}>
            <Select options={methods.map((m) => ({ value: m, label: t(`wms.method.${m}`) }))} />
          </Form.Item>
          <Form.Item name="maxOrders" label={t("wms.pool.maxOrders")} extra={t("wms.wave.maxOrdersHelp")}>
            <InputNumber min={1} max={5000} precision={0} style={{ width: 160 }} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}
